import { useEffect } from "react";
import { useRouter } from "next/router";

/**
 * A cross-page anchor link (e.g. footer FAQ -> /product#faq) lands at the top
 * of the new page and then smooth-scrolls down to the anchor — instead of the
 * browser gliding from the previous page's scroll offset through unrelated
 * content. Same-page anchors (hashChange events), back/forward restores, and
 * reduced-motion users are untouched.
 */
export function useCrossPageAnchorGlide() {
  const router = useRouter();

  useEffect(() => {
    let poppedState = false;

    const markPop = () => {
      poppedState = true;
    };

    const handleComplete = (url: string) => {
      const popped = poppedState;
      poppedState = false;
      const hash = url.split("#")[1];
      if (hash === undefined || popped) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      // Next positions the page at the anchor right after emitting this
      // event; this rAF runs after that but before the next paint, so the
      // reposition to top is never visible as a flash.
      requestAnimationFrame(() => {
        const target = document.getElementById(decodeURIComponent(hash));
        if (target === null) return;
        window.scrollTo({ top: 0, behavior: "instant" });
        target.scrollIntoView({ behavior: "smooth" });
      });
    };

    // A popstate that resolves to anything other than a completed route change
    // must not leave the flag set for the next navigation.
    const clearPop = () => {
      poppedState = false;
    };

    window.addEventListener("popstate", markPop);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("hashChangeComplete", clearPop);
    router.events.on("routeChangeError", clearPop);
    return () => {
      window.removeEventListener("popstate", markPop);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("hashChangeComplete", clearPop);
      router.events.off("routeChangeError", clearPop);
    };
  }, [router.events]);
}
